import React, { useState, useEffect, useRef } from "react";
import "./ActionPopup.css";
import { IoCloseOutline } from "react-icons/io5";
import { RiDeleteBinLine } from "react-icons/ri";

const ACTION_LABEL = {
  add:    "Add",
  edit:   "Save Changes",
  delete: "Delete",
};

const ActionPopup = ({ type, title, defaultValue, onConfirm, onCancel, inputType = "text" }) => {
  const [inputValue, setInputValue] = useState(defaultValue || "");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const inputRef = useRef(null);

  // Helper to convert AM/PM format back to 24h for input type="time"
  const parseTime = (timeStr) => {
    if (!timeStr) return "";
    const [time, modifier] = timeStr.split(" ");
    if (!modifier) return timeStr; // Already 24h
    let [hours, minutes] = time.split(":");
    if (hours === "12") hours = "00";
    if (modifier.toUpperCase() === "PM") hours = parseInt(hours, 10) + 12;
    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  useEffect(() => {
    if (type === "add" || type === "edit") {
      setInputValue(defaultValue || "");
      if (inputType === "timeRange" && defaultValue) {
        const parts = (defaultValue || "").split(" - ");
        if (parts.length === 2) {
          setStartTime(parseTime(parts[0].trim()));
          setEndTime(parseTime(parts[1].trim()));
        }
      }
    }
  }, [type, defaultValue, inputType]);

  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter")  handleSubmit();
    if (e.key === "Escape") onCancel();
  };

  const handleSubmit = () => {
    if (type === "add" || type === "edit") {
      if (inputType === "timeRange") {
        if (!startTime || !endTime) return;
        
        const formatTime = (timeStr) => {
          if (!timeStr) return "";
          if (timeStr.toLowerCase().includes("am") || timeStr.toLowerCase().includes("pm")) return timeStr;
          
          let [h, m] = timeStr.split(":");
          if (!h || !m) return timeStr;
          let hour = parseInt(h, 10);
          const ampm = hour >= 12 ? "PM" : "AM";
          hour = hour % 12 || 12;
          return `${hour.toString().padStart(2, '0')}:${m} ${ampm}`;
        };

        onConfirm(`${formatTime(startTime)} - ${formatTime(endTime)}`);
        return;
      }
      
      if (!inputValue.trim()) return;
      onConfirm(inputValue.trim());
      return;
    }
    
    // For delete
    onConfirm(inputValue);
  };

  const isInputType  = type === "add" || type === "edit";
  const modalTitle   = isInputType
    ? `${type === "add" ? "Add New" : "Edit"} ${title}`
    : `Delete ${title}`;
  const confirmLabel = ACTION_LABEL[type] || "Confirm";

  return (
    <div
      className="popup-overlay"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label={modalTitle}
    >
      <div
        className="popup-card"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* ── Header ── */}
        <div className="popup-header">
          <h2 className="popup-title">{modalTitle}</h2>
          <button className="popup-close-btn" onClick={onCancel} aria-label="Close">
            <IoCloseOutline />
          </button>
        </div>

        <div className="popup-divider" />

        {/* ── Body ── */}
        <div className="popup-body">
          {isInputType && (
            <div className="popup-field">
              <label className="popup-label" htmlFor="popup-input">
                {title}
              </label>
              {inputType === "timeRange" ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <input
                    type="time"
                    className="popup-input"
                    value={startTime}
                    onChange={(e) => setStartTime(e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <span style={{ fontWeight: 500, color: "#666" }}>to</span>
                  <input
                    type="time"
                    className="popup-input"
                    value={endTime}
                    onChange={(e) => setEndTime(e.target.value)}
                    style={{ flex: 1 }}
                  />
                </div>
              ) : (
                <input
                  id="popup-input"
                  ref={inputRef}
                  type="text"
                  className="popup-input"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={`Enter ${title}`}
                  autoComplete="off"
                />
              )}
            </div>
          )}

          {type === "delete" && (
            <div className="popup-delete-body">
              <div className="popup-delete-icon-wrap">
                <RiDeleteBinLine />
              </div>
              <p className="popup-delete-msg">
                Are you sure you want to delete{" "}
                <strong>"{defaultValue}"</strong>?
              </p>
              <p className="popup-delete-sub">This action cannot be undone.</p>
            </div>
          )}
        </div>

        <div className="popup-divider" />

        {/* ── Footer ── */}
        <div className="popup-footer">
          <button className="popup-btn popup-btn--cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            className={`popup-btn popup-btn--confirm${type === "delete" ? " popup-btn--danger" : ""}`}
            onClick={handleSubmit}
            disabled={
              isInputType &&
              (inputType === "timeRange"
                ? !startTime || !endTime
                : !inputValue.trim())
            }
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActionPopup;
